import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { validateOrdinaryPostAuthNext } from '@/lib/auth-redirect';

export default function AuthCallback() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'error'>('loading');
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const next = params.get('next');

    async function run() {
      if (!code) {
        if (!cancelled) {
          setStatus('error');
          setError('Missing authorization code. Please try signing in again.');
        }
        return;
      }

      const { data, error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);

      if (cancelled) return;

      if (exchangeError) {
        setStatus('error');
        setError(exchangeError.message);
        return;
      }

      // This app is passwordless, so a recovery result should never occur;
      // still guard against it to avoid a dead-end navigation.
      if (data?.redirectType === 'recovery') {
        navigate('/dashboard', { replace: true });
        return;
      }

      const pendingCode = localStorage.getItem('ctrader_pending_oauth_code');
      if (pendingCode) {
        navigate('/dashboard?code=' + encodeURIComponent(pendingCode), { replace: true });
      } else {
        const target = validateOrdinaryPostAuthNext(next);
        navigate(target === '/' ? '/dashboard' : target, { replace: true });
      }
    }

    run();

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  return (
    <div className="min-h-screen bg-background-50 flex items-center justify-center px-4">
      <div className="bg-background-100 rounded-xl border border-background-200/70 p-8 w-full max-w-md text-center">
        {status === 'loading' ? (
          <div className="flex flex-col items-center gap-4">
            <div className="w-10 h-10 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-foreground-800 text-sm font-medium">Signing you in...</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center">
              <i className="ri-error-warning-line text-4xl text-amber-400 w-12 h-12 flex items-center justify-center"></i>
            </div>
            <div>
              <p className="text-foreground-800 font-semibold text-sm mb-1">Sign in failed</p>
              <p className="text-foreground-500 text-xs">{error}</p>
            </div>
            <button
              onClick={() => navigate('/login', { replace: true })}
              className="px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap"
            >
              Back to Sign In
            </button>
          </div>
        )}
      </div>
    </div>
  );
}